/**
 * Vuex Module — Códigos de Descuento
 * ───────────────────────────────────
 * Gestiona el estado global del módulo: listado, filtros, item activo,
 * loading flags y operaciones CRUD delegando al service layer.
 */

import discountCodesService from "@/services/discountCodes.service";

const state = {
  /** Lista de códigos (resultado del último fetch) */
  codes: [],
  /** Total de registros (para paginación futura) */
  totalCodes: 0,
  /** Código seleccionado (detalle / edición) */
  currentCode: null,
  /** Historial de usos del código seleccionado */
  usages: [],
  totalUsages: 0,
  /** Flags de carga */
  loading: false,
  loadingDetail: false,
  loadingUsages: false,
  saving: false,
  /** Filtros activos */
  filters: {
    search: "",
    type: "",
    status: "",
    startDate: "",
    endDate: "",
  },
};

const getters = {
  discountCodes: (state) => state.codes,
  totalDiscountCodes: (state) => state.totalCodes,
  currentDiscountCode: (state) => state.currentCode,
  discountCodeUsages: (state) => state.usages,
  totalDiscountCodeUsages: (state) => state.totalUsages,
  discountCodesLoading: (state) => state.loading,
  discountCodeDetailLoading: (state) => state.loadingDetail,
  discountCodeUsagesLoading: (state) => state.loadingUsages,
  discountCodeSaving: (state) => state.saving,
  discountCodesFilters: (state) => state.filters,
};

const mutations = {
  SET_CODES(state, { data, total }) {
    state.codes = data;
    state.totalCodes = total;
  },
  SET_CURRENT_CODE(state, code) {
    state.currentCode = code;
  },
  SET_USAGES(state, { data, total }) {
    state.usages = data;
    state.totalUsages = total;
  },
  SET_LOADING(state, val) {
    state.loading = val;
  },
  SET_LOADING_DETAIL(state, val) {
    state.loadingDetail = val;
  },
  SET_LOADING_USAGES(state, val) {
    state.loadingUsages = val;
  },
  SET_SAVING(state, val) {
    state.saving = val;
  },
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  RESET_FILTERS(state) {
    state.filters = { search: "", type: "", status: "", startDate: "", endDate: "" };
  },
  /** Actualiza un código en la lista sin refetch */
  UPDATE_CODE_IN_LIST(state, updatedCode) {
    const idx = state.codes.findIndex((c) => c.id === updatedCode.id);
    if (idx !== -1) {
      state.codes.splice(idx, 1, updatedCode);
    }
  },
  REMOVE_CODE_FROM_LIST(state, id) {
    state.codes = state.codes.filter((c) => c.id !== id);
    state.totalCodes = Math.max(0, state.totalCodes - 1);
  },
};

const actions = {
  // ─── Fetch listado ──────────────────────────────────────────
  async fetchDiscountCodes({ commit, state }) {
    commit("SET_LOADING", true);
    try {
      const result = await discountCodesService.getAll(state.filters);
      commit("SET_CODES", result);
    } catch (error) {
      console.error("Error al obtener códigos de descuento:", error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // ─── Fetch detalle ──────────────────────────────────────────
  async fetchDiscountCode({ commit }, id) {
    commit("SET_LOADING_DETAIL", true);
    try {
      const result = await discountCodesService.getById(id);
      commit("SET_CURRENT_CODE", result.data);
      return result.data;
    } catch (error) {
      console.error("Error al obtener detalle del código:", error);
      throw error;
    } finally {
      commit("SET_LOADING_DETAIL", false);
    }
  },

  // ─── Fetch historial de usos ───────────────────────────────
  async fetchDiscountCodeUsages({ commit }, { id, params }) {
    commit("SET_LOADING_USAGES", true);
    try {
      const result = await discountCodesService.getUsages(id, params);
      commit("SET_USAGES", result);
    } catch (error) {
      console.error("Error al obtener historial de usos:", error);
      throw error;
    } finally {
      commit("SET_LOADING_USAGES", false);
    }
  },

  // ─── Crear ──────────────────────────────────────────────────
  async createDiscountCode({ commit, dispatch }, payload) {
    commit("SET_SAVING", true);
    try {
      const result = await discountCodesService.create(payload);
      await dispatch("fetchDiscountCodes");
      return result.data;
    } catch (error) {
      console.error("Error al crear código de descuento:", error);
      throw error;
    } finally {
      commit("SET_SAVING", false);
    }
  },

  // ─── Actualizar ─────────────────────────────────────────────
  async updateDiscountCode({ commit, dispatch }, { id, payload }) {
    commit("SET_SAVING", true);
    try {
      const result = await discountCodesService.update(id, payload);
      commit("UPDATE_CODE_IN_LIST", result.data);
      commit("SET_CURRENT_CODE", result.data);
      return result.data;
    } catch (error) {
      console.error("Error al actualizar código de descuento:", error);
      throw error;
    } finally {
      commit("SET_SAVING", false);
    }
  },

  // ─── Cambiar estado ─────────────────────────────────────────
  async toggleDiscountCodeStatus({ commit }, { id, status }) {
    try {
      const result = await discountCodesService.updateStatus(id, status);
      commit("UPDATE_CODE_IN_LIST", result.data);
      return result.data;
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      throw error;
    }
  },

  // ─── Eliminar ───────────────────────────────────────────────
  async deleteDiscountCode({ commit }, id) {
    try {
      await discountCodesService.remove(id);
      commit("REMOVE_CODE_FROM_LIST", id);
    } catch (error) {
      console.error("Error al eliminar código:", error);
      throw error;
    }
  },

  // ─── Filtros ────────────────────────────────────────────────
  setDiscountCodesFilters({ commit, dispatch }, filters) {
    commit("SET_FILTERS", filters);
    return dispatch("fetchDiscountCodes");
  },

  resetDiscountCodesFilters({ commit, dispatch }) {
    commit("RESET_FILTERS");
    return dispatch("fetchDiscountCodes");
  },

  clearCurrentDiscountCode({ commit }) {
    commit("SET_CURRENT_CODE", null);
    commit("SET_USAGES", { data: [], total: 0 });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
